import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, iif, combineLatest } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { LoggingService } from '../../shared/services/core/logging.service';
import { MemberQuery } from '../../account/member/member.query';
import { MemberCardQuery } from '../../account/member-card/member-card.query';
import { ProductCodeService } from './product-code.service';
import { ProductCodeQuery } from './product-code.query';
import { MemberCardService } from '../../account/member-card/member-card.service';

export interface ProductGuardOptions {
  exclude?: string[];
  include?: string[];
}

/** to use
 *  {
 *    path: 'home'
 *    component: HomePageComponent,
 *    canActivate: [ ProductCodeGuard ]
 *    data: {
 *            exclude: ['VIP_1', 'SESN_2'],
 *            codesRedirect: ['viphome', 'seasontickethome']
 *          }
 *    OR
 *    data: {
 *            include: ['VIP_1', 'SESN_2'],
 *            codesRedirect: 'some/page'
 *          }
 *  }
 */
@Injectable()
export class ProductCodeGuard  {
  constructor(
    private loggingService: LoggingService,
    private memberQuery: MemberQuery,
    private memberCardQuery: MemberCardQuery,
    private memberCardService: MemberCardService,
    private productCodeQuery: ProductCodeQuery,
    private productCodeService: ProductCodeService,
    private router: Router
  ) {}
  exclude: string[];
  include: string[];
  codesRedirect: string | string[];

  fetchDataFromRoute(route: ActivatedRouteSnapshot) {
    this.exclude = route.data.excludeCodes as string[];
    this.include = route.data.includeCodes as string[];
    this.codesRedirect = route.data.codesRedirect as string | string[];

    if (!this.codesRedirect === undefined || this.codesRedirect === null) {
      throw new Error('you must include a redirect');
    }
    if (this.include && this.exclude) {
      throw new Error('either include or exclude product codes, not both.');
    }
    if ((!this.include || !this.include.length) && (!this.exclude || !this.exclude.length)) {
      throw new Error(`the product code guard for ${route.url} has no product codes specified.`);
    }
    if (typeof this.codesRedirect !== 'string' && this.include) {
      throw new Error('inclusive product code guard can only have one redirect');
    }
    if (
      typeof this.codesRedirect !== 'string' &&
      this.codesRedirect.length > 1 &&
      this.codesRedirect.length !== this.exclude.length
    ) {
      throw new Error(
        'exclusive product code guard needs a redirect for each excluded code or a single redirect for all product codes'
      );
    }
  }

  getRedirect() {
    if (typeof this.codesRedirect === 'string') {
      return this.router.parseUrl(this.codesRedirect);
    } else if (this.codesRedirect.length === 1) {
      return this.router.parseUrl(this.codesRedirect[0]);
    } else {
      const userPop = this.memberCardQuery.getEntity(this.memberQuery.getUserContext().memberId)?.ProductCode;
      if (!userPop) {
        return undefined;
      }

      const redirect = this.codesRedirect[this.exclude.indexOf(userPop)];
      return this.router.parseUrl(redirect);
    }
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    this.fetchDataFromRoute(route);

    const productCodesFetch$ = this.productCodeService.fetchProductCodes();
    const memberCardFetch$ = this.memberCardService.fetchCard();

    return combineLatest([productCodesFetch$, memberCardFetch$]).pipe(
      switchMap(() =>
        iif(
          () => !!this.include,
          this.productCodeQuery.selectCurrentUserIsProductCode(this.include),
          this.productCodeQuery.selectCurrentUserIsNotProductCode(this.exclude)
        )
      ),
      tap((x) =>
        this.loggingService.debug(
          'product code guard pass: ',
          x.toString(),
          'product code guard type: ',
          !!this.include ? 'include' : 'exclude'
        )
      ),
      map((allowed) => {
        if (allowed) {
          return true;
        }

        let redirect = this.getRedirect();
        if (!redirect) {
          return false;
        }
        return redirect;
      })
    );
  }
}
